import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../components/Navbar";
import AIChatbot from "../components/AIChatbot";
import { JAPAN_REQS } from "../utils/helpers";

const SEE_MORE_LIMIT = 120;

function FormattedText({ text }) {
  if (!text) return null;
  return (
    <span>
      {text.split("\n").map((line, li) => (
        <span key={li}>
          {li > 0 && <br />}
          {line.split(/(\*\*[^*]+\*\*)/).map((part, i) =>
            part.startsWith("**") && part.endsWith("**")
              ? <strong key={i}>{part.slice(2, -2)}</strong>
              : <span key={i}>{part}</span>
          )}
        </span>
      ))}
    </span>
  );
}

function JobDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const plain = text.replace(/\*\*/g, "");
  const isLong = plain.length > SEE_MORE_LIMIT;
  const displayText = !isLong || expanded ? text : text.substring(0, SEE_MORE_LIMIT) + "…";
  return (
    <p className="job-card-desc">
      <FormattedText text={displayText} />
      {isLong && (
        <button onClick={() => setExpanded(!expanded)}
          style={{ background: "none", border: "none", color: "var(--gold-600)", fontWeight: 600, fontSize: 13, cursor: "pointer", padding: 0, marginLeft: 4 }}>
          {expanded ? "See less" : "See more"}
        </button>
      )}
    </p>
  );
}

const DEFAULT_HIRINGS = [
  { id: "default-jp", country: "Japan", flag: "🇯🇵", title: "Skilled Workers", description: "Laundry, Baking, Food Processing & more", openings: 25, requirements: JAPAN_REQS },
];

export default function HomePage() {
  const [hirings, setHirings] = useState(DEFAULT_HIRINGS);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "hirings"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          if (a.sortOrder !== undefined && b.sortOrder !== undefined) return a.sortOrder - b.sortOrder;
          if (a.sortOrder !== undefined) return -1;
          if (b.sortOrder !== undefined) return 1;
          return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
        });
        if (list.length > 0) setHirings(list);
      } catch (e) { console.error(e); }
    })();
  }, []);

  return (
    <>
      <Navbar />
      <section className="hero">
        <div className="container hero-content">
          <span className="hero-eyebrow">Cebu City, Philippines</span>
          <h1 className="hero-title">
            Your Gateway to <span className="gold">Global</span> Opportunities
          </h1>
          <p className="hero-subtitle">
            Golden Gateway International Agency endorses qualified Filipino workers for premier overseas employment opportunities in Japan and beyond. Trusted, certified, and built for your success.
          </p>
          <div className="hero-actions">
            <Link to="/apply" className="btn btn-primary"><i className="ti ti-file-plus"></i> Request Endorsement</Link>
            <Link to="/track" className="btn btn-outline" style={{ color: "var(--cream)", borderColor: "var(--gold-400)" }}>
              <i className="ti ti-search"></i> Track Endorsement
            </Link>
          </div>
        </div>
      </section>

      <section className="jobs-section">
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span className="hero-eyebrow" style={{ color: "var(--gold-600)", borderColor: "var(--gold-500)" }}>NOW HIRING</span>
            <h2 style={{ fontSize: 40, marginTop: 12 }}>Available Job Endorsements</h2>
            <p className="text-muted" style={{ maxWidth: 600, margin: "10px auto 0" }}>
              Open positions for Filipino workers seeking overseas employment.
            </p>
          </div>

          <div className="jobs-grid">
            {hirings.map((j) => (
              <article key={j.id} className="job-card">
                {j.bannerUrl
                  ? <img src={j.bannerUrl} alt={j.title} className="job-card-banner-img" />
                  : <div className="job-card-banner"></div>
                }
                <div className="job-card-body">
                  <div className="job-card-flag">{j.flag || "🌏"}</div>
                  <div className="job-card-country">{j.country}</div>
                  <h3 className="job-card-title">{j.title}</h3>
                  <JobDescription text={j.description} />
                  <div className="job-card-meta">
                    {j.openings && <span><i className="ti ti-users"></i> {j.openings} openings</span>}
                  </div>
                  <div style={{
                    display: "flex", alignItems: "flex-start", gap: 8,
                    padding: "10px 12px", background: "#fff8e8",
                    border: "1px solid #f5d989", borderRadius: 8, marginBottom: 14,
                    fontSize: 12, color: "#7a5c1e", lineHeight: 1.5
                  }}>
                    <i className="ti ti-info-circle" style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}></i>
                    <span>
                      <strong>Please note:</strong> Available slots may already be occupied by applicants currently in the lineup.
                      If no slots are immediately available, you will be placed in the waiting list and prioritized
                      once a slot opens. We encourage you to apply early to secure your spot.
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-outline" onClick={() => setModal(j)}>
                      <i className="ti ti-list-details"></i> View Requirements
                    </button>
                    <Link to="/apply" className="btn btn-primary"><i className="ti ti-arrow-right"></i> Request Endorsement</Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {modal && (
        <div className="modal-backdrop" onClick={() => setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div style={{ fontSize: 11, color: "var(--gold-600)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  {modal.country} {modal.flag}
                </div>
                <h2 style={{ fontSize: 24 }}>{modal.title} — Requirements</h2>
              </div>
              <button className="modal-close" onClick={() => setModal(null)}><i className="ti ti-x"></i></button>
            </div>
            <div className="modal-body">
              <ol style={{ paddingLeft: 20 }}>
                {(modal.requirements || JAPAN_REQS).map((r, i) => (
                  <li key={i} style={{ marginBottom: 10, lineHeight: 1.6 }}>{r}</li>
                ))}
              </ol>
              <div style={{ marginTop: 24, padding: 16, background: "var(--cream-warm)", borderRadius: "var(--radius-md)" }}>
                <strong>📞 Ready to request your endorsement?</strong> Click "Request Endorsement" below.
                <div style={{ marginTop: 12 }}>
                  <Link to="/apply" className="btn btn-primary"><i className="ti ti-file-plus"></i> Request Endorsement Now</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer style={{ background: "var(--ink-900)", color: "var(--ink-200)", padding: "40px 0", textAlign: "center" }}>
        <div className="container">
          <div style={{ fontFamily: "var(--font-display)", fontSize: 18, color: "var(--gold-300)", marginBottom: 8 }}>
            Golden Gateway International Agency
          </div>
          <div style={{ fontSize: 13 }}>Don Gil Garcia Street, Capitol Site, Cebu City</div>
          <div style={{ fontSize: 12, marginTop: 16, color: "var(--ink-400)" }}>© {new Date().getFullYear()} Golden Gateway International Agency.</div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--ink-700)" }}>
            <span style={{ fontSize: 11, color: "var(--ink-500)" }}>Developed by </span>
            <span style={{
              fontSize: 12, fontWeight: 700, fontFamily: "var(--font-mono)",
              background: "linear-gradient(135deg, var(--gold-400), var(--gold-600))",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              backgroundClip: "text", letterSpacing: "0.05em"
            }}>
              Gerald Durano
            </span>
          </div>
        </div>
      </footer>

      <AIChatbot />
    </>
  );
}
